import { queryOptions, useQuery } from '@tanstack/react-query';

import type { RespondentMessageThreadDto } from '@/api/core-api/types.gen';

import { respondentMessagesControllerGetThread } from '@/api/core-api/sdk.gen';
import { getClient } from '@/utils/get-api-client';

import { queryKeys } from './query-keys';

/**
 * Poll cadence for the thread while it feeds the nav badge — matches
 * `INBOX_REFETCH_INTERVAL_MS` in `use-participant-inbox` so both halves of
 * the badge refresh together. Respondent messages have no realtime channel,
 * so polling is the only way a new one surfaces outside the inbox.
 */
const THREAD_REFETCH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Query options for the signed-in participant's one-way "Respondent" thread.
 *
 * Shared by the Messages page (pinned row + thread pane) and the layout nav
 * badge, and prefetched from the authenticated layout `beforeLoad` (with the
 * signed-in user id for server auth) so the badge doesn't pop in after
 * paint. `staleTime` matches the poll window and `placeholderData` keeps the
 * last thread across background refetches so the badge doesn't flicker.
 */
export function respondentMessagesQueryOptions(userId?: string) {
  return queryOptions({
    queryKey: queryKeys.respondentMessages.mine,
    queryFn: async (): Promise<RespondentMessageThreadDto> => {
      const response = await respondentMessagesControllerGetThread({
        client: getClient(userId),
      });

      if (response.error || !response.data) {
        throw new Error('Failed to load Respondent messages');
      }

      return response.data;
    },
    refetchInterval: THREAD_REFETCH_INTERVAL_MS,
    staleTime: THREAD_REFETCH_INTERVAL_MS,
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/**
 * The signed-in participant's one-way "Respondent" thread — messages sent by
 * Respondent (the company) to this user, pinned above the conversation list.
 * An empty thread means the pinned row is hidden entirely, so errors are
 * silent by design (`retry: false`, no error UI): a failed fetch just hides
 * the pinned thread until the next successful load.
 *
 * `unreadCount` also feeds the Messages nav badge (combined with the
 * conversation inbox count in the authenticated participant layout).
 */
export function useRespondentMessages() {
  const query = useQuery(respondentMessagesQueryOptions());

  return {
    ...query,
    messages: query.data?.results,
    unreadCount: query.data?.unreadCount ?? 0,
  };
}
